import { ChevronRight } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { HeroCard } from "../components/HeroCard";
import { Reveal, SectionCard, SectionHeader } from "../components/Section";
import { JOY_TINT } from "../lib/joy";
import { ziggySrc } from "../lib/ziggy";
import { useZiggyVariant } from "../lib/ziggyContext";
import { COMMUNICATION, FEE, FEE_ICON, OVERVIEW_STATS, UPDATES } from "../lib/mockData";

export function ParentHome({ firstName, childName }: { firstName: string; childName: string }) {
  const childFirst = childName.split(" ")[0] ?? childName;
  const FeeIcon = FEE_ICON;
  const { variant } = useZiggyVariant();

  return (
    <div className="space-y-5 lg:space-y-6">
      <HeroCard
        variant="parent"
        title={<>Hello, {firstName}!</>}
        subtitle={`Welcome to ${childFirst}'s parent portal`}
        mascotSrc={ziggySrc(variant, "grown", "happy")}
        mascotAlt="Ziggy waving hello"
      />

      <div className="space-y-5 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0 lg:[&>*]:min-w-0">
        {/* Child overview */}
        <Reveal index={1}>
          <SectionHeader title={`${childFirst}'s Overview`} action="This term" />
          <div className="grid grid-cols-2 gap-3">
            {OVERVIEW_STATS.map((stat) => (
              <SectionCard key={stat.label} className="flex items-center gap-3">
                <span
                  className={cn("grid size-11 place-items-center rounded-xl", JOY_TINT[stat.color])}
                >
                  <stat.icon className="size-5" />
                </span>
                <div className="leading-tight">
                  <p className="font-display text-xl font-semibold">{stat.value}</p>
                  <p className="text-muted-foreground text-xs">{stat.label}</p>
                </div>
              </SectionCard>
            ))}
          </div>
        </Reveal>

        {/* Recent updates */}
        <Reveal index={2}>
          <SectionHeader title="Recent Updates" action="View all" />
          <SectionCard className="divide-border/60 divide-y p-0">
            {UPDATES.map((u) => (
              <div key={u.title} className="flex items-center gap-3 px-4 py-3">
                <span
                  className={cn(
                    "grid size-10 shrink-0 place-items-center rounded-xl",
                    JOY_TINT[u.color],
                  )}
                >
                  <u.icon className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{u.title}</p>
                  <p className="text-muted-foreground truncate text-xs">{u.detail}</p>
                </div>
                <span className="text-muted-foreground shrink-0 text-[0.7rem]">{u.when}</span>
              </div>
            ))}
          </SectionCard>
        </Reveal>

        {/* Fee status */}
        <Reveal index={3} className="lg:col-span-2">
          <SectionHeader title="Fee Status" action="View details" />
          <SectionCard className="hero-parent hero-orbs relative overflow-hidden border-0 text-white">
            <div className="relative z-10 flex items-center gap-4">
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm">
                <FeeIcon className="size-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-white/80">Term 2 fees</p>
                <p className="font-display text-2xl font-semibold leading-tight">{FEE.amount}</p>
                <p className="text-xs text-white/80">{FEE.dueLabel}</p>
              </div>
              <button
                type="button"
                className="bg-card text-primary shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-[var(--shadow-sm)] transition-transform duration-150 ease-out active:scale-[0.97]"
              >
                Pay now
              </button>
            </div>
          </SectionCard>
        </Reveal>

        {/* Communication */}
        <Reveal index={4} className="lg:col-span-2">
          <SectionHeader title="Communication" />
          <div className="grid grid-cols-4 gap-3">
            {COMMUNICATION.map((item) => (
              <button
                key={item.label}
                type="button"
                className="border-border/60 bg-card flex flex-col items-center gap-2 rounded-2xl border p-3 text-center shadow-[var(--shadow-card)] transition-transform duration-150 ease-out hover:-translate-y-0.5 active:scale-[0.97]"
              >
                <span
                  className={cn("grid size-11 place-items-center rounded-xl", JOY_TINT[item.color])}
                >
                  <item.icon className="size-5" />
                </span>
                <span className="text-[0.7rem] font-medium leading-tight">{item.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        {/* Parents' corner */}
        <Reveal index={5} className="lg:col-span-2">
          <div className="hero-celebrate hero-orbs relative overflow-hidden rounded-[1.5rem] p-5 text-white shadow-[var(--shadow-float)]">
            <div className="relative z-10 flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-semibold leading-tight">
                  Parents&apos; Corner
                </p>
                <p className="mt-1 text-sm text-white/85">
                  Tips, articles and resources to support {childFirst} at home.
                </p>
                <button
                  type="button"
                  className="mt-3 inline-flex items-center gap-1 rounded-xl bg-white/20 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition-transform duration-150 ease-out hover:bg-white/25 active:scale-[0.98]"
                >
                  Explore now <ChevronRight className="size-4" />
                </button>
              </div>
              <img
                src={ziggySrc(variant, "grown", "cheerful")}
                alt=""
                aria-hidden
                className="-my-3 h-32 w-24 shrink-0 object-contain drop-shadow-xl"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
